import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft, Save, Send, CheckCircle2, XCircle, BookCheck,
  RotateCcw, Ban, Trash2, AlertTriangle, CheckCircle,
  FileText, History, Paperclip, ShieldCheck, Search, X, Printer,
  ExternalLink, Receipt,
} from 'lucide-react';

import { bankChargeApi } from '@/api/bankCharge.api';
import { bankAccountApi } from '@/api/bank.api';
import { accountApi } from '@/api/account.api';
import { exchangeRateApi } from '@/api/exchangeRate.api';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { BankChargeVoucher } from '@/components/banking/BankChargeVoucher';
import { exportToExcel } from '@/utils/reportExport';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import { AccountLedgerButton, ledgerShortcut } from '@/components/erp/AccountLedgerButton';
import { ERPContextBar } from '@/components/erp/ERPContextBar';
import { WorkflowStatusPanel } from '@/components/erp/WorkflowStatusPanel';
import { AttachmentPanel } from '@/components/erp/AttachmentPanel';
import { AuditViewer } from '@/components/erp/AuditViewer';
import { setErpContext, clearErpContext } from '@/state/erpContextSlice';
import { cn } from '@/utils/cn';

import type { RootState } from '@/state/store';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { SearchColumn } from '@/components/master/MasterSearchModal';
import type { BankAccountListDto } from '@/types/bank.types';
import type { AccountListDto } from '@/types/account.types';
import type {
  BankChargeDto,
  BankChargeWorkflowStatus,
  CreateBankChargeRequest,
  UpdateBankChargeRequest,
} from '@/types/bankCharge.types';
import { BNK_CHARGE_WORKFLOW_STATUS_COLORS } from '@/types/bankCharge.types';

// ── Search columns ─────────────────────────────────────────────────────────────

const BANK_ACCOUNT_COLUMNS: SearchColumn<BankAccountListDto>[] = [
  { key: 'accountNumber', label: 'Account No.', className: 'w-32 font-mono text-teal-700 dark:text-teal-400' },
  { key: 'accountName',   label: 'Account Name' },
  { key: 'currencyCode',  label: 'CCY', className: 'w-14 font-mono text-slate-500 dark:text-gray-400' },
  { key: 'accountType',   label: 'Type', className: 'w-20' },
];

const GL_ACCOUNT_COLUMNS: SearchColumn<AccountListDto>[] = [
  { key: 'code',        label: 'Code',         className: 'w-28 font-mono text-teal-700 dark:text-teal-400' },
  { key: 'name',        label: 'Account Name' },
  { key: 'accountType', label: 'Type',         className: 'w-24' },
];

// ── Tabs ───────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'details',     label: 'Details',    icon: FileText    },
  { key: 'workflow',    label: 'Workflow',    icon: History     },
  { key: 'attachments', label: 'Attachments', icon: Paperclip   },
  { key: 'audit',       label: 'Audit Log',   icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Action modal ───────────────────────────────────────────────────────────────

interface ActionModalProps {
  title: string; placeholder: string; required: boolean;
  onConfirm: (text: string) => void; onCancel: () => void;
}

const ActionModal = ({ title, placeholder, required, onConfirm, onCancel }: ActionModalProps) => {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">{title}</h3>
        <textarea ref={ref} value={text} onChange={e => setText(e.target.value)} placeholder={placeholder} rows={4}
          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-sm
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none
                     focus:ring-2 focus:ring-teal-500 outline-none" />
        <div className="flex gap-3 mt-4 justify-end">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600
                       hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">
            Cancel
          </button>
          <button onClick={() => onConfirm(text)} disabled={required && !text.trim()}
            className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// ── GL Account Picker component ────────────────────────────────────────────────

interface GlPickerProps {
  label: string; accountId: string; accountCode: string; accountName: string;
  required?: boolean; editable: boolean; onOpen: () => void; onClear: () => void;
}

const GlPicker = ({ label, accountId, accountCode, accountName, required, editable, onOpen, onClear }: GlPickerProps) => (
  <div onKeyDownCapture={ledgerShortcut(accountId)}>
    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div
      tabIndex={accountId ? 0 : -1}
      onClick={() => editable && onOpen()}
      className={cn(
        'flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors',
        'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600',
        editable ? 'cursor-pointer hover:border-teal-400' : 'cursor-default',
        !accountId && 'text-slate-400',
      )}
    >
      {accountId ? (
        <>
          <span className="font-mono text-xs font-semibold text-teal-700 dark:text-teal-400">{accountCode}</span>
          <span className="text-slate-700 dark:text-slate-300 truncate flex-1 text-xs">{accountName}</span>
          <AccountLedgerButton accountId={accountId} />
          {editable && (
            <button type="button" onClick={e => { e.stopPropagation(); onClear(); }}
              className="text-slate-300 hover:text-red-500"><X size={12} /></button>
          )}
        </>
      ) : (
        <>
          {editable && <Search size={12} className="text-slate-300" />}
          <span className="text-xs italic">{editable ? 'Select GL account…' : '—'}</span>
        </>
      )}
    </div>
  </div>
);

const fieldClass = (disabled: boolean) => cn(
  'w-full px-3 py-2 text-sm border rounded-lg outline-none transition-colors',
  'bg-white dark:bg-slate-700 text-slate-900 dark:text-white',
  'border-slate-200 dark:border-slate-600',
  disabled
    ? 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-gray-400 cursor-default'
    : 'focus:ring-2 focus:ring-teal-500 focus:border-transparent',
);

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Main Component ─────────────────────────────────────────────────────────────

export const BankChargeFormPage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew    = !id;

  const { hasPermission } = usePermission();
  const canEdit    = hasPermission(Permissions.BNK_CHARGE_EDIT);
  const canSubmit  = hasPermission(Permissions.BNK_CHARGE_SUBMIT);
  const canApprove = hasPermission(Permissions.BNK_CHARGE_APPROVE);
  const canReject  = hasPermission(Permissions.BNK_CHARGE_REJECT);
  const canPost    = hasPermission(Permissions.BNK_CHARGE_POST);
  const canReverse = hasPermission(Permissions.BNK_CHARGE_REVERSE);
  const canCancel  = hasPermission(Permissions.BNK_CHARGE_CANCEL);
  const canDelete  = hasPermission(Permissions.BNK_CHARGE_DELETE);
  const canPrint   = hasPermission(Permissions.BNK_CHARGE_PRINT);
  const canAddDoc  = hasPermission(Permissions.DOC_ATTACHMENT_ADD);
  const canDelDoc  = hasPermission(Permissions.DOC_ATTACHMENT_DELETE);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const session    = useSelector((s: RootState) => s.session.session);
  const accessibleCostCenters = useSelector((s: RootState) => s.auth.accessibleCostCenters);

  const today = new Date().toISOString().split('T')[0];
  const baseCurrency = authUser?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD';

  // ── Form state ────────────────────────────────────────────────────────────
  const [chargeDate,         setChargeDate]         = useState(today);
  const [postingDate,        setPostingDate]         = useState(today);
  const [bankAccountId,      setBankAccountId]       = useState('');
  const [bankAccountNumber,  setBankAccountNumber]   = useState('');
  const [bankAccountName,    setBankAccountName]     = useState('');
  const [bankAccountCode,    setBankAccountCode]     = useState('');
  const [chargeType,         setChargeType]          = useState('');
  const [expenseAccountId,   setExpenseAccountId]    = useState('');
  const [expenseAccountCode, setExpenseAccountCode]  = useState('');
  const [expenseAccountName, setExpenseAccountName]  = useState('');
  const [inputTaxAccountId,  setInputTaxAccountId]   = useState('');
  const [inputTaxAccountCode, setInputTaxAccountCode] = useState('');
  const [inputTaxAccountName, setInputTaxAccountName] = useState('');
  const [amount,             setAmount]              = useState<number>(0);
  const [taxRatePct,         setTaxRatePct]          = useState<number>(0);   // 0-100
  const [exchangeRate,       setExchangeRate]        = useState(1);
  const [currencyCode,       setCurrencyCode]        = useState(baseCurrency);
  const [description,        setDescription]         = useState('');
  const [reference,          setReference]           = useState('');
  const [costCenterId,       setCostCenterId]        = useState('');

  // Computed
  const taxRate   = taxRatePct / 100;
  const taxAmount = amount * taxRate;
  const totalAmount = amount + taxAmount;
  const baseAmount  = totalAmount * exchangeRate;

  const [bankOpen,       setBankOpen]       = useState(false);
  const [expenseOpen,    setExpenseOpen]    = useState(false);
  const [inputTaxOpen,   setInputTaxOpen]   = useState(false);

  const [charge,    setCharge]    = useState<BankChargeDto | null>(null);
  const [loading,   setLoading]   = useState(!isNew);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [success,   setSuccess]   = useState<string | null>(null);
  const [tab,       setTab]       = useState<Tab>('details');
  const [modal,     setModal]     = useState<'approve' | 'reject' | 'reverse' | 'cancel' | 'delete' | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  useEffect(() => {
    if (!isNew) return;
    setCostCenterId(session?.costCenterId ?? '');
  }, [isNew, session?.costCenterId]);

  useEffect(() => {
    if (!currencyCode || currencyCode === baseCurrency) {
      if (isNew) setExchangeRate(1);
      return;
    }
    exchangeRateApi.getList({ fromCurrencyCode: baseCurrency, toCurrencyCode: currencyCode, pageSize: 10 })
      .then(res => {
        const items = res.data?.data?.items ?? [];
        const sorted = items
          .filter((r: { effectiveDate: string }) => r.effectiveDate <= chargeDate)
          .sort((a: { effectiveDate: string }, b: { effectiveDate: string }) => b.effectiveDate.localeCompare(a.effectiveDate));
        if (sorted.length > 0) setExchangeRate(sorted[0].rate);
        else if (items.length > 0) setExchangeRate(items[0].rate);
      }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode, chargeDate]);

  const fetchBankAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await bankAccountApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  const fetchGlAccounts = async (search: string, page: number, pageSize: number) => {
    const res = await accountApi.getList({ search, pageNumber: page, pageSize, isActive: true } as Parameters<typeof accountApi.getList>[0]);
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadCharge = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true); setError(null);
    try {
      const res = await bankChargeApi.getById(id);
      const c = res.data?.data;
      if (!c) throw new Error('Charge not found');
      setCharge(c);

      setChargeDate(c.chargeDate.split('T')[0]);
      setPostingDate(c.postingDate.split('T')[0]);
      setBankAccountId(c.bankAccountId);
      setBankAccountCode(c.bankAccountCode);
      setBankAccountName(c.bankAccountName);
      setChargeType(c.chargeType);
      setExpenseAccountId(c.expenseAccountId ?? '');
      setExpenseAccountCode(c.expenseAccountCode ?? '');
      setExpenseAccountName(c.expenseAccountName ?? '');
      setInputTaxAccountId(c.inputTaxAccountId ?? '');
      setInputTaxAccountCode(c.inputTaxAccountCode ?? '');
      setInputTaxAccountName(c.inputTaxAccountName ?? '');
      setCurrencyCode(c.currencyCode);
      setExchangeRate(c.exchangeRate);
      setAmount(c.amount);
      setTaxRatePct(c.taxRate * 100);
      setDescription(c.narration ?? '');
      setReference(c.reference ?? '');
      setCostCenterId(c.costCenterId ?? '');

      bankAccountApi.getById(c.bankAccountId)
        .then(r => { if (r.data?.data) setBankAccountNumber(r.data.data.accountNumber); })
        .catch(() => {});

      dispatch(setErpContext({
        companyCode:         authUser?.companyId?.substring(0, 8).toUpperCase() ?? 'COMPANY',
        companyName:         '',
        locationCode:        null, locationName: null,
        costCenterCode:      null, costCenterName: null,
        financialYearCode:   c.financialYearCode,
        financialPeriodCode: c.financialPeriodCode,
        transactionTypeCode: 'BNK-CHG',
        documentNumber:      c.chargeNumber,
        currencyCode:        c.currencyCode,
        exchangeRate:        c.exchangeRate,
      }));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load charge');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch, authUser]);

  useEffect(() => { loadCharge(); }, [loadCharge]);
  useEffect(() => { return () => { dispatch(clearErpContext()); }; }, [dispatch]);

  const status = (charge?.workflowStatus ?? 'Draft') as BankChargeWorkflowStatus;
  const isDraftEditable = isNew || status === 'Draft' || status === 'Rejected';
  const isEditable = isDraftEditable && (isNew ? hasPermission(Permissions.BNK_CHARGE_CREATE) : canEdit);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    if (!bankAccountId) { setError('Please select a bank account.'); return; }
    if (!chargeType.trim()) { setError('Charge type is required.'); return; }
    if (amount <= 0) { setError('Amount must be greater than zero.'); return; }

    setSaving(true);
    try {
      if (isNew) {
        const payload: CreateBankChargeRequest = {
          companyId:           authUser?.companyId ?? '',
          locationId:          null,
          costCenterId:        costCenterId || null,
          financialYearCode:   session?.financialYearCode ?? '',
          financialPeriodCode: session?.financialPeriodCode ?? '',
          chargeDate,
          postingDate,
          bankAccountId,
          chargeType,
          expenseAccountId:  expenseAccountId || null,
          inputTaxAccountId: inputTaxAccountId || null,
          currencyCode,
          exchangeRate,
          amount,
          taxRate,
          reference:  reference || null,
          narration:  description || null,
        };
        const res = await bankChargeApi.create(payload);
        const newId = res.data?.data?.id;
        setSuccess('Bank charge created successfully.');
        setTimeout(() => navigate(`/banking/charges/${newId}`), 800);
      } else if (charge) {
        const payload: UpdateBankChargeRequest = {
          chargeDate,
          postingDate,
          bankAccountId,
          bankAccountCode,
          bankAccountName,
          bankGLAccountId:      null,
          chargeType,
          expenseAccountId:     expenseAccountId || null,
          expenseAccountCode:   expenseAccountCode || null,
          expenseAccountName:   expenseAccountName || null,
          inputTaxAccountId:    inputTaxAccountId || null,
          inputTaxAccountCode:  inputTaxAccountCode || null,
          inputTaxAccountName:  inputTaxAccountName || null,
          currencyCode,
          exchangeRate,
          amount,
          taxRate,
          reference:  reference || null,
          narration:  description || null,
        };
        await bankChargeApi.update(charge.id, payload);
        setSuccess('Bank charge updated.');
        setTimeout(() => { setSuccess(null); loadCharge(); }, 800);
      }
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save charge.');
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow ───────────────────────────────────────────────────────────────
  const doAction = async (action: () => Promise<void>, successMsg: string) => {
    setError(null); setSaving(true);
    try {
      await action();
      setSuccess(successMsg);
      setTimeout(() => { setSuccess(null); loadCharge(); }, 1200);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Action failed.');
    } finally { setSaving(false); }
  };

  const handleSubmit  = () => doAction(async () => { await bankChargeApi.submit(charge!.id); }, 'Charge submitted for approval.');
  const handlePost    = () => doAction(async () => { await bankChargeApi.post(charge!.id); }, 'Charge posted to general ledger.');
  const handleApprove = (r: string) => doAction(async () => { await bankChargeApi.approve(charge!.id, r); }, 'Charge approved.');
  const handleReject  = (r: string) => doAction(async () => { await bankChargeApi.reject(charge!.id, r); }, 'Charge rejected.');
  const handleCancel  = (r: string) => doAction(async () => { await bankChargeApi.cancel(charge!.id, r); }, 'Charge cancelled.');
  const handleReverse = (narr: string) => doAction(async () => {
    const res = await bankChargeApi.reverse(charge!.id, today, today, narr || undefined);
    const revId = res.data?.data?.id;
    setTimeout(() => navigate(`/banking/charges/${revId}`), 1200);
  }, 'Reversal charge created.');

  const handleDelete = async () => {
    if (!charge) return;
    setSaving(true); setError(null);
    try {
      await bankChargeApi.delete(charge.id);
      navigate(AppRoutes.BANK_CHARGES, { replace: true });
    } catch (e: unknown) {
      setError((e as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        ?? (e as { message?: string })?.message ?? 'Delete failed.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">
      <ERPContextBar />

      {/* Toolbar */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => navigate(AppRoutes.BANK_CHARGES)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                       hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            <ArrowLeft size={14} /> Back
          </button>
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />

          {isEditable && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              {saving ? <Spinner size="sm" /> : <Save size={14} />} Save Draft
            </button>
          )}
          {!isNew && status === 'Draft' && canSubmit && (
            <button onClick={handleSubmit} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Send size={14} /> Submit for Approval
            </button>
          )}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canApprove && (
            <button onClick={() => setModal('approve')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <CheckCircle2 size={14} /> Approve
            </button>
          )}
          {!isNew && (status === 'Submitted' || status === 'PendingApproval') && canReject && (
            <button onClick={() => setModal('reject')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <XCircle size={14} /> Reject
            </button>
          )}
          {!isNew && status === 'Approved' && canPost && (
            <button onClick={handlePost} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <BookCheck size={14} /> Post to Ledger
            </button>
          )}
          {!isNew && status === 'Posted' && canReverse && !charge?.reversalChargeId && (
            <button onClick={() => setModal('reverse')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <RotateCcw size={14} /> Reverse
            </button>
          )}
          {!isNew && !['Posted', 'Cancelled', 'Reversed'].includes(status) && canCancel && (
            <button onClick={() => setModal('cancel')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-rose-600 hover:bg-rose-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Ban size={14} /> Cancel
            </button>
          )}
          {!isNew && status === 'Draft' && canDelete && (
            <button onClick={() => setModal('delete')} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700
                         text-white rounded-lg disabled:opacity-50 transition-colors font-medium">
              <Trash2 size={14} /> Delete
            </button>
          )}
          {!isNew && charge && canPrint && (
            <button onClick={() => setPrintOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <Printer size={14} /> Print
            </button>
          )}
          {!isNew && (
            <div className="ml-auto flex items-center gap-2">
              {charge?.isReversal && (
                <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold rounded-full">Reversal</span>
              )}
              <span className={cn('px-3 py-1 rounded-full text-xs font-semibold',
                BNK_CHARGE_WORKFLOW_STATUS_COLORS[status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                {status}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}
        {success && (
          <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20
                          border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-400">
            <CheckCircle size={14} className="shrink-0" /><span>{success}</span>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Receipt size={18} className="text-teal-600 dark:text-teal-400" />
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {isNew ? 'New Bank Charge' : `Bank Charge ${charge?.chargeNumber ?? ''}`}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {!isNew && charge?.originalChargeId && (
              <button onClick={() => navigate(`/banking/charges/${charge.originalChargeId}`)}
                className="text-xs text-teal-600 dark:text-teal-400 hover:underline flex items-center gap-1">
                View Original <ExternalLink size={10} />
              </button>
            )}
            {!isNew && charge?.reversalChargeId && (
              <button onClick={() => navigate(`/banking/charges/${charge.reversalChargeId}`)}
                className="text-xs text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1">
                View Reversal <ExternalLink size={10} />
              </button>
            )}
            {!isNew && charge?.sourceGlJournalId && (
              <button onClick={() => navigate(`/gl/journals/${charge.sourceGlJournalId}`)}
                className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1">
                View GL Journal <ExternalLink size={10} />
              </button>
            )}
          </div>
        </div>
        {!isNew && charge && (
          <p className="text-xs text-slate-400">Created {new Date(charge.createdDate).toLocaleString()}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6">
        <div className="flex">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                tab === t.key
                  ? 'border-teal-600 text-teal-600 dark:text-teal-400'
                  : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-gray-600',
              )}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab body */}
      <div className="flex-1 overflow-auto">
        {tab === 'details' && (
          <div className="p-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* Transaction Date */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Transaction Date <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={chargeDate} onChange={e => setChargeDate(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)} />
                </div>

                {/* Posting Date */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Posting Date <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={postingDate} onChange={e => setPostingDate(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)} />
                </div>

                {/* Bank Account */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Bank Account <span className="text-red-500">*</span>
                  </label>
                  <div
                    onClick={() => isEditable && setBankOpen(true)}
                    className={cn(
                      'flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors',
                      'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600',
                      isEditable ? 'cursor-pointer hover:border-teal-400' : 'cursor-default',
                      !bankAccountId && 'text-slate-400',
                    )}
                  >
                    {bankAccountId ? (
                      <>
                        <span className="font-mono text-xs font-semibold text-teal-700 dark:text-teal-400">{bankAccountNumber || bankAccountCode}</span>
                        <span className="text-slate-700 dark:text-slate-300 truncate flex-1 text-xs">{bankAccountName}</span>
                        {isEditable && (
                          <button type="button" onClick={e => { e.stopPropagation(); setBankAccountId(''); setBankAccountNumber(''); setBankAccountName(''); setBankAccountCode(''); }}
                            className="text-slate-300 hover:text-red-500"><X size={12} /></button>
                        )}
                      </>
                    ) : (
                      <>
                        {isEditable && <Search size={12} className="text-slate-300" />}
                        <span className="text-xs italic">{isEditable ? 'Select bank account…' : '—'}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Charge Type */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Charge Type <span className="text-red-500">*</span>
                  </label>
                  <input value={chargeType} onChange={e => setChargeType(e.target.value)}
                    disabled={!isEditable} placeholder="e.g. Monthly Fee, Wire Fee…" className={fieldClass(!isEditable)} />
                </div>

                {/* Expense GL Account */}
                <div>
                  <GlPicker
                    label="Expense GL Account"
                    accountId={expenseAccountId}
                    accountCode={expenseAccountCode}
                    accountName={expenseAccountName}
                    required
                    editable={isEditable}
                    onOpen={() => setExpenseOpen(true)}
                    onClear={() => { setExpenseAccountId(''); setExpenseAccountCode(''); setExpenseAccountName(''); }}
                  />
                </div>

                {/* Currency */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Currency</label>
                  <input value={currencyCode} onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
                    disabled={!isEditable} maxLength={10}
                    className={cn(fieldClass(!isEditable), 'font-mono uppercase')} />
                </div>

                {/* Amount (pre-tax) */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Amount (pre-tax) <span className="text-red-500">*</span>
                  </label>
                  <input type="number" value={amount || ''} onChange={e => setAmount(parseFloat(e.target.value) || 0)}
                    disabled={!isEditable} step="0.01" min="0"
                    className={cn(fieldClass(!isEditable), 'text-right font-mono')} />
                </div>

                {/* Tax Rate % */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Tax Rate %</label>
                  <input type="number" value={taxRatePct} onChange={e => setTaxRatePct(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                    disabled={!isEditable} step="0.01" min="0" max="100"
                    className={cn(fieldClass(!isEditable), 'text-right font-mono')} />
                </div>

                {/* Tax Amount (computed) */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Tax Amount <span className="text-xs text-slate-400">(computed)</span>
                  </label>
                  <div className={cn(fieldClass(true), 'font-mono text-right')}>
                    {fmt(taxAmount)}
                  </div>
                </div>

                {/* Input Tax GL Account (conditional) */}
                {taxRatePct > 0 && (
                  <div>
                    <GlPicker
                      label="Input Tax GL Account"
                      accountId={inputTaxAccountId}
                      accountCode={inputTaxAccountCode}
                      accountName={inputTaxAccountName}
                      editable={isEditable}
                      onOpen={() => setInputTaxOpen(true)}
                      onClear={() => { setInputTaxAccountId(''); setInputTaxAccountCode(''); setInputTaxAccountName(''); }}
                    />
                  </div>
                )}

                {/* Total Amount (computed) */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Total Amount <span className="text-xs text-slate-400">(computed)</span>
                  </label>
                  <div className={cn(fieldClass(true), 'font-mono text-right font-semibold text-teal-700 dark:text-teal-400')}>
                    {fmt(totalAmount)}
                  </div>
                </div>

                {/* Exchange Rate */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Exchange Rate</label>
                  <input type="number" value={exchangeRate} onChange={e => setExchangeRate(parseFloat(e.target.value) || 1)}
                    disabled={!isEditable} step="0.000001" min="0.000001" className={fieldClass(!isEditable)} />
                </div>

                {/* Base Amount (computed) */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Base Amount <span className="text-xs text-slate-400">(computed)</span>
                  </label>
                  <div className={cn(fieldClass(true), 'font-mono text-right font-semibold')}>
                    {fmt(baseAmount)}
                  </div>
                </div>

                {/* Reference */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Reference</label>
                  <input value={reference} onChange={e => setReference(e.target.value)}
                    disabled={!isEditable} placeholder="Optional" className={fieldClass(!isEditable)} />
                </div>

                {/* Cost Centre */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Cost Centre</label>
                  <select value={costCenterId} onChange={e => setCostCenterId(e.target.value)}
                    disabled={!isEditable} className={fieldClass(!isEditable)}>
                    <option value="">— None —</option>
                    {costCentresForLocation(accessibleCostCenters, session?.locationId).map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
                  </select>
                </div>

                {/* Financial Period */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Financial Period</label>
                  <div className={fieldClass(true)}>
                    {charge?.financialPeriodCode ?? session?.financialPeriodCode ?? '—'}
                  </div>
                </div>

                {/* Description */}
                <div className="lg:col-span-3">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Description</label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)}
                    disabled={!isEditable} rows={2} placeholder="Describe this charge…"
                    className={cn(fieldClass(!isEditable), 'resize-none')} />
                </div>

              </div>
            </div>
          </div>
        )}

        {tab === 'workflow' && (
          <div className="p-6">
            {!isNew && charge ? (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Approval Status</h3>
                <WorkflowStatusPanel entityName="BankCharge" entityId={charge.id} />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <History size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the charge first to track workflow.</p>
              </div>
            )}
          </div>
        )}

        {tab === 'attachments' && (
          <div className="p-6">
            {!isNew && charge ? (
              <AttachmentPanel entityName="BankCharge" entityId={charge.id} canAdd={canAddDoc} canDelete={canDelDoc} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Paperclip size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the charge first to add attachments.</p>
              </div>
            )}
          </div>
        )}

        {tab === 'audit' && (
          <div className="p-6">
            {!isNew && charge ? (
              <AuditViewer entityName="BankCharge" entityId={charge.id} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <ShieldCheck size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the charge first to view audit history.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action modals */}
      {modal === 'approve' && (
        <ActionModal title="Approve Charge" placeholder="Approval remarks (optional)…" required={false}
          onConfirm={text => { setModal(null); handleApprove(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reject' && (
        <ActionModal title="Reject Charge" placeholder="Rejection reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleReject(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reverse' && (
        <ActionModal title="Reverse Charge" placeholder="Reversal narration (optional)…" required={false}
          onConfirm={text => { setModal(null); handleReverse(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'cancel' && (
        <ActionModal title="Cancel Charge" placeholder="Cancellation reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleCancel(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'delete' && (
        <ActionModal title="Delete Draft Charge" placeholder="The draft is cancelled and kept for audit. It can no longer be edited or posted." required={false}
          onConfirm={() => { setModal(null); handleDelete(); }} onCancel={() => setModal(null)} />
      )}

      {/* Search modals */}
      <MasterSearchModal<BankAccountListDto>
        isOpen={bankOpen}
        onClose={() => setBankOpen(false)}
        title="Select Bank Account"
        columns={BANK_ACCOUNT_COLUMNS}
        fetchData={fetchBankAccounts}
        onSelect={item => {
          setBankAccountId(item.id);
          setBankAccountNumber(item.accountNumber);
          setBankAccountName(item.accountName);
          setBankAccountCode(item.accountNumber);
          if (item.currencyCode) setCurrencyCode(item.currencyCode);
          setBankOpen(false);
        }}
      />

      <MasterSearchModal<AccountListDto>
        isOpen={expenseOpen}
        onClose={() => setExpenseOpen(false)}
        title="Select Expense GL Account"
        columns={GL_ACCOUNT_COLUMNS}
        fetchData={fetchGlAccounts}
        onSelect={item => {
          setExpenseAccountId(item.id);
          setExpenseAccountCode(item.code);
          setExpenseAccountName(item.name);
          setExpenseOpen(false);
        }}
      />

      <MasterSearchModal<AccountListDto>
        isOpen={inputTaxOpen}
        onClose={() => setInputTaxOpen(false)}
        title="Select Input Tax GL Account"
        columns={GL_ACCOUNT_COLUMNS}
        fetchData={fetchGlAccounts}
        onSelect={item => {
          setInputTaxAccountId(item.id);
          setInputTaxAccountCode(item.code);
          setInputTaxAccountName(item.name);
          setInputTaxOpen(false);
        }}
      />

      {/* Print */}
      {charge && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Bank Charge ${charge.chargeNumber}`}
          fileBaseName={`Bank-Charge-${charge.chargeNumber}`}
          email={{ documentType: 'Bank Charge', documentNo: charge.chargeNumber }}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Bank Charge',
            title: `Bank Charge — ${charge.chargeNumber}`,
            columns: [
              { header: 'Field', key: 'field', width: 28 },
              { header: 'Value', key: 'value', width: 40 },
            ],
            rows: [
              { field: 'Document No',   value: charge.chargeNumber ?? '' },
              { field: 'Charge Date',   value: charge.chargeDate?.slice(0, 10) ?? '' },
              { field: 'Posting Date',  value: charge.postingDate?.slice(0, 10) ?? '' },
              { field: 'Bank Account',  value: `${charge.bankAccountCode ?? ''} ${charge.bankAccountName ?? ''}`.trim() },
              { field: 'Charge Type',   value: charge.chargeType ?? '' },
              { field: 'Expense Account', value: `${charge.expenseAccountCode ?? ''} ${charge.expenseAccountName ?? ''}`.trim() },
              { field: 'Amount (pre-tax)', value: charge.amount ?? 0 },
              { field: 'Tax Rate %',    value: charge.taxRate ?? 0 },
              { field: 'Tax Amount',    value: charge.taxAmount ?? 0 },
              { field: 'Total Amount',  value: charge.totalAmount ?? 0 },
              { field: 'Currency',      value: charge.currencyCode ?? '' },
              { field: 'Exchange Rate', value: charge.exchangeRate ?? 1 },
              { field: 'Base Amount',   value: charge.baseAmount ?? 0 },
              { field: 'Reference',     value: charge.reference ?? '' },
              { field: 'Narration',     value: charge.narration ?? '' },
            ],
          }, `Bank-Charge-${charge.chargeNumber}`)}
        >
          <BankChargeVoucher
            charge={charge}
            companyName={authUser?.companyId ? '' : 'myLedger ONE'}
            printedBy={authUser?.username}
          />
        </MasterReportPreview>
      )}
    </div>
  );
};
